// src/Components/Common/CountDown/CountDown.jsx

import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Clock, X } from 'lucide-react';
import { removeCountdown } from '../../../Redux/Slices/Pages/Client/countDown';
import './CountDown.css';

const CountDown = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { activeCountdowns } = useSelector(state => state.CountDown);
  const [currentCountdown, setCurrentCountdown] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  // Get most recent countdown
  useEffect(() => {
    if (activeCountdowns && activeCountdowns.length > 0) {
      const clientCountdowns = activeCountdowns.filter(c => c.userRole === 'client');
      if (clientCountdowns.length > 0) {
        setCurrentCountdown(clientCountdowns[clientCountdowns.length - 1]);
      } else {
        setCurrentCountdown(null);
      }
    } else {
      setCurrentCountdown(null);
      setTimeLeft(null);
    }
  }, [activeCountdowns]);

  // Calculate time
  useEffect(() => {
    if (!currentCountdown) return;

    const calculateTime = () => {
      const now = Date.now();
      const expiry = new Date(currentCountdown.expiresAt).getTime();
      const remaining = expiry - now;

      if (remaining <= 0) {
        dispatch(removeCountdown({ 
          bookingId: currentCountdown.bookingId,
          userRole: currentCountdown.userRole 
        }));
        setTimeLeft(null);
        return;
      }

      const minutes = Math.floor(remaining / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
      const totalMinutes = currentCountdown.type === 'client_payment' ? 30 : 10;
      const percentage = Math.max(0, (remaining / (totalMinutes * 60 * 1000)) * 100);

      setTimeLeft({ minutes, seconds, percentage });
    };

    calculateTime();
    const timer = setInterval(calculateTime, 1000);
    return () => clearInterval(timer);
  }, [currentCountdown, dispatch]);

  const handleClick = () => {
    if (currentCountdown) {
      navigate(`/myBooking/personal/requested`);
    }
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    if (window.confirm('Hide countdown? You can still view your booking.')) {
      dispatch(removeCountdown({ 
        bookingId: currentCountdown.bookingId,
        userRole: currentCountdown.userRole 
      }));
    }
  };

  if (!currentCountdown || !timeLeft) return null;

  const { metadata } = currentCountdown;
  const { minutes, seconds, percentage } = timeLeft;

  // Get color based on percentage
  const getColor = () => {
    if (percentage > 50) return '#4CAF50'; // Green
    if (percentage > 20) return '#FF9800'; // Orange
    return '#f44336'; // Red
  };

  const color = getColor();

  return (
    <div 
      className="countdown-top-banner"
      onClick={handleClick}
      style={{ 
        background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`
      }}
    >
      <div className="countdown-top-content">
        <Clock size={20} className="countdown-icon" />
        
        <div className="countdown-info">
          <span className="countdown-time">
            {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
          </span>
          <span className="countdown-message">
            {metadata?.providerName || 'Provider'} • #{metadata?.bookingNumber}
          </span>
        </div>

        <button 
          className="countdown-close"
          onClick={handleDismiss}
          aria-label="Dismiss"
        >
          <X size={18} />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="countdown-progress">
        <div 
          className="countdown-progress-fill"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export default CountDown;
